import { Routes } from "@angular/router";

import { AuthGuard } from "guards/auth.guard";
import { NonCorityGuard } from "guards/cority.guard";
import { OktaAuthGuard } from "guards/okta-auth.guard";
import { ViewerGuard } from "guards/viewer.guard";
import { orgResolver } from "resolvers/org.resolver";

export const APP_ROUTES: Routes = [
  {
    path: "",
    redirectTo: "login",
    pathMatch: "full",
  },
  {
    path: "login",
    canActivate: [OktaAuthGuard],
    loadComponent: () =>
      import("../login/login.component").then((mod) => mod.LoginComponent),
  },
  {
    path: "forgot-password",
    loadComponent: () =>
      import("../forgotPassword/forgotPassword.component").then(
        (mod) => mod.ForgotPasswordComponent
      ),
  },
  {
    path: "learn",
    canMatch: [AuthGuard],
    loadComponent: () =>
      import("../learn/learn.component").then((mod) => mod.LearnComponent),
  },
  {
    path: "help",
    canMatch: [AuthGuard],
    loadComponent: () =>
      import("../help/help.component").then((mod) => mod.HelpComponent),
  },
  {
    path: "video-library",
    canMatch: [AuthGuard],
    loadComponent: () =>
      import("../videoLibrary/videoLibrary.component").then(
        (mod) => mod.VideoLibraryComponent
      ),
  },
  {
    path: "dev",
    canMatch: [AuthGuard, ViewerGuard],
    loadComponent: () => import("../dev/dev.component").then((mod) => mod.DevComponent),
  },
  {
    path: "admin",
    canMatch: [AuthGuard, ViewerGuard],
    resolve: {
      isOrgResolved: orgResolver,
    },
    /**
     * Dev mode switches org by merging `orgId` into the query params.
     * `trackedQueryParams` below only recreates the component,
     * but resolvers re-execution is controlled by `runGuardsAndResolvers`.
     */
    runGuardsAndResolvers: "paramsOrQueryParamsChange",
    loadComponent: () =>
      import("../org-admin/org-admin.component").then((mod) => mod.OrgAdminComponent),
    data: {
      trackedQueryParams: ["orgId"],
    },
  },
  {
    path: "employee",
    canActivate: [AuthGuard],
    loadComponent: () =>
      import("../employee/employee.component").then((mod) => mod.EmployeeComponent),
  },
  {
    path: "upload",
    canMatch: [AuthGuard, NonCorityGuard, ViewerGuard],
    loadChildren: () =>
      import("../upload/upload.routes").then((mod) => mod.UPLOAD_ROUTES),
  },
  {
    path: "organization",
    redirectTo: "employees-list",
    pathMatch: "full",
  },
  {
    path: "employees-list",
    canMatch: [AuthGuard, NonCorityGuard],
    loadChildren: () =>
      import("../employees-list/employees-list.routes").then(
        (mod) => mod.EMPLOYEES_LIST_ROUTES
      ),
    data: {
      trackedQueryParams: ["orgId"],
    },
  },
  {
    path: "report",
    canMatch: [AuthGuard],
    loadChildren: () =>
      import("../report/report.routes").then((mod) => mod.REPORT_ROUTES),
    data: {
      trackedQueryParams: ["orgId"],
    },
  },
  {
    path: "compare",
    canMatch: [AuthGuard, NonCorityGuard],
    loadComponent: () =>
      import("../compare/compare.component").then((mod) => mod.CompareComponent),
  },
  {
    path: "homepage",
    canMatch: [AuthGuard, NonCorityGuard],
    loadChildren: () =>
      import("../analytics/analytics-dashboard.routes").then(
        (mod) => mod.ANALYTICS_DASHBOARD_ROUTES
      ),
    data: {
      trackedQueryParams: ["orgId"],
    },
  },
  {
    path: "reports",
    redirectTo: "reports-list",
    pathMatch: "full",
  },
  {
    path: "reports-list",
    canMatch: [AuthGuard],
    loadChildren: () =>
      import("../reports-list/reports-list.routes").then((mod) => mod.REPORT_LIST_ROUTES),
    data: {
      trackedQueryParams: ["orgId"],
    },
  },
  {
    path: "shared-report",
    loadChildren: () =>
      import("../sharedReport/shared-report.routes").then(
        (mod) => mod.SHARED_REPORT_ROUTES
      ),
  },
  {
    path: "okta-callback",
    loadComponent: () =>
      import("../oktaCallback/oktaCallback.component").then(
        (mod) => mod.OktaCallbackComponent
      ),
  },
  {
    path: "okta-fallback",
    loadComponent: () =>
      import("../oktaFallback/oktaFallback.component").then(
        (mod) => mod.OktaFallbackComponent
      ),
  },
  {
    path: "client-auth",
    loadComponent: () =>
      import("../client-auth/client-auth.component").then(
        (mod) => mod.ClientAuthComponent
      ),
  },
  {
    path: "**",
    loadComponent: () =>
      import("./page-not-found/page-not-found.component").then(
        (mod) => mod.PageNotFoundComponent
      ),
  },
];
